// Turns the hero script into a list of frames.
//
// A frame is one still picture of the card - what is in the composer, which
// messages are showing, how far the answer has streamed, where the proposal
// card is - plus how long it stays up. The player then only has to step an
// index on a timer, and pausing is simply not stepping. Everything that
// decides what the card looks like at a given moment is in here, pure, which
// is what lets it be tested without a browser or fake timers.
//
// Frames are a few hundred for the whole loop, built once. Cheaper than it
// sounds: each one shares every finished message with the frame before it.

// Milliseconds. Typing is quicker than a person types - the point is to show
// a question being asked, not to make the reader wait for it.
export const TIMING = {
  beforeTyping: 700,
  perChar: 28,
  afterSend: 450,
  toolRunning: 850,
  toolDone: 250,
  perWord: 55,
  afterAnswer: 1300,
  proposalShown: 1300,
  proposalPressing: 300,
  proposalConfirmed: 2600,
  fadeOut: 500,
}

function withLast(items, item) {
  return [...items.slice(0, -1), item]
}

// The frames of one scene, from an empty card to its last picture, then a
// fading frame. `scene` is the index, carried on every frame so the card can
// remount between scenes rather than animate one into the other.
function sceneFrames(beats, scene, t) {
  const frames = []
  let items = []
  const push = (duration, extra = {}) =>
    frames.push({ scene, items, composer: '', fading: false, duration, ...extra })

  for (const beat of beats) {
    switch (beat.type) {
      case 'user': {
        push(t.beforeTyping)
        for (let i = 1; i <= beat.text.length; i++) {
          push(t.perChar, { composer: beat.text.slice(0, i) })
        }
        items = [...items, { kind: 'user', text: beat.text }]
        push(t.afterSend)
        break
      }
      case 'tools': {
        // One row per call, each shown running and then done, so the calls
        // land one after another the way the agent makes them.
        const done = []
        for (const call of beat.calls) {
          const rowsRunning = [...done, { ...call, status: 'running' }]
          items = done.length === 0
            ? [...items, { kind: 'tools', calls: rowsRunning }]
            : withLast(items, { kind: 'tools', calls: rowsRunning })
          push(t.toolRunning)
          done.push({ ...call, status: 'complete' })
          items = withLast(items, { kind: 'tools', calls: [...done] })
          push(t.toolDone)
        }
        break
      }
      case 'assistant': {
        const words = beat.text.split(' ')
        items = [...items, { kind: 'assistant', text: '', streaming: true }]
        for (let i = 1; i <= words.length; i++) {
          items = withLast(items, {
            kind: 'assistant',
            text: words.slice(0, i).join(' '),
            streaming: i < words.length,
          })
          push(i < words.length ? t.perWord : t.afterAnswer)
        }
        break
      }
      case 'proposal': {
        const card = { kind: 'proposal', proposal: beat }
        items = [...items, { ...card, state: 'shown' }]
        push(t.proposalShown)
        items = withLast(items, { ...card, state: 'pressing' })
        push(t.proposalPressing)
        items = withLast(items, { ...card, state: 'confirmed' })
        push(t.proposalConfirmed)
        break
      }
      default:
        throw new Error(`hero script: unknown beat type "${beat.type}"`)
    }
  }
  push(t.fadeOut, { fading: true })
  return frames
}

export function buildFrames(scenes, t = TIMING) {
  return scenes.flatMap((beats, i) => sceneFrames(beats, i, t))
}

// The card at rest: every scene's last picture, one after the other. What a
// reader who asked for reduced motion sees, and what assistive technology
// reads instead of the animation.
export function finalItems(scenes) {
  return scenes.flatMap((beats, i) => {
    const frames = sceneFrames(beats, i, TIMING)
    return frames[frames.length - 1].items
  })
}
